import React, { useState, useEffect } from 'react';
import { Card } from '../ui/Card';
import { format, subHours } from 'date-fns';
import ApexChart from 'react-apexcharts';
import { apexLineChartDefaultOption } from '../../configs/chart.config';
import type { ApexOptions } from 'apexcharts';
import Select from '@/components/ui/Select/Select';
import { SingleValue, ActionMeta } from 'react-select';
import { Button } from '@/components/ui/Button';
import DateTimePicker from '@/components/ui/dateTimePicker';

interface TimeSeriesPoint {
  timestamp: string;
  value: number;
}

interface TimeSeriesData {
  metric: string;
  points: TimeSeriesPoint[];
}

interface YAxisAnnotation {
  y: number;
  y2?: number;
  borderColor?: string;
  fillColor?: string;
  opacity?: number;
  strokeDashArray?: number;
  label?: {
    text: string;
    borderColor?: string;
    style?: {
      color?: string;
      background?: string;
    };
  };
}

interface TimeSeriesChartProps {
  deviceId: string;
  startTime: string;
  endTime: string;
  interval?: string;
  height?: number;
  className?: string;
  title?: string;
  subtitle?: string;
  data?: TimeSeriesData[];
  showTitle?: boolean;
  onIntervalChange?: (interval: string) => void;
  onDateRangeChange?: (startTime: string, endTime: string) => void;
  metricKey?: string;
  metricKeys?: string[];
  yAxisTitle?: string;
  tooltipValueSuffix?: string;
  chartColor?: string;
  chartColors?: string[];
  seriesName?: string;
  seriesNames?: string[];
  yAxisAnnotations?: YAxisAnnotation[];
  // Optional overrides (defaults keep existing behaviour)
  curve?: 'smooth' | 'straight' | 'stepline';
  yMin?: number;
  yMax?: number;
  yTickAmount?: number;
  // Formats values on the y-axis and in the tooltip (e.g. enum value -> label)
  valueFormatter?: (value: number) => string;
  yLabelMaxWidth?: number;
}

const TimeSeriesChart: React.FC<TimeSeriesChartProps> = ({
  deviceId,
  startTime,
  endTime,
  interval = '1m',
  height = 600,
  className,
  title = 'Time Series Chart',
  subtitle = 'Monitor values over time',
  data = [],
  showTitle = true,
  onIntervalChange,
  onDateRangeChange,
  metricKey = 'wifi_strength',
  metricKeys,
  yAxisTitle = 'Value',
  tooltipValueSuffix = '',
  chartColor = '#4f46e5',
  chartColors,
  seriesName = 'Value',
  seriesNames,
  yAxisAnnotations = [],
  curve = 'smooth',
  yMin,
  yMax,
  yTickAmount,
  valueFormatter,
  yLabelMaxWidth,
}) => {
  const [selectedInterval, setSelectedInterval] = useState<string>(interval);
  const [timeRange, setTimeRange] = useState<string>(getTimeRangeFromDates(startTime, endTime));
  const [isAbsoluteTime, setIsAbsoluteTime] = useState<boolean>(() => {
    // Try to get saved preference from localStorage
    const savedPreference = localStorage.getItem(`timeRangeType_${deviceId}`);
    return savedPreference === 'absolute';
  });
  const [absoluteStartTime, setAbsoluteStartTime] = useState<string>(formatDateTimeForInput(startTime));
  const [absoluteEndTime, setAbsoluteEndTime] = useState<string>(formatDateTimeForInput(endTime));
  const [timeError, setTimeError] = useState<string | null>(null);
  
  useEffect(() => {
    setSelectedInterval(interval);
  }, [interval]);
  
  useEffect(() => {
    const newTimeRange = getTimeRangeFromDates(startTime, endTime);
    if (newTimeRange) {
      setTimeRange(newTimeRange);
    }
    
    setAbsoluteStartTime(formatDateTimeForInput(startTime));
    setAbsoluteEndTime(formatDateTimeForInput(endTime));
  }, [startTime, endTime]);
  
  function formatDateTimeForInput(dateTimeString: string): string {
    try {
      const date = new Date(dateTimeString);
      
      // Fix timezone issue by using local timezone formatting instead of UTC
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      
      return `${year}-${month}-${day}T${hours}:${minutes}`;
    } catch (error) {
      console.error('Error formatting date for input:', error);
      return '';
    }
  }
  
  function getTimeRangeFromDates(start: string, end: string): string {
    try {
      const startDate = new Date(start);
      const endDate = new Date(end);
      const diffHours = (endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60);
      
      if (diffHours <= 1) return '1h';
      if (diffHours <= 6) return '6h';
      if (diffHours <= 12) return '12h';
      if (diffHours <= 24) return '24h';
      if (diffHours <= 48) return '48h';
      if (diffHours <= 168) return '7d';
      return '24h';
    } catch (error) {
      console.error('Error calculating time range:', error);
      return '24h';
    }
  }
  
  const formatTimestamp = (timestamp: string): string => {
    try {
      // For ApexCharts multi-line labels, we need to return the date as is
      // The formatter in the xaxis config will handle the formatting
      return timestamp;
    } catch (error) {
      console.error('Error formatting timestamp:', error);
      return timestamp;
    }
  };

  const prepareChartData = () => {
    if (!data.length) return { series: [], categories: [] };
    
    const effectiveMetricKeys = Array.isArray(metricKeys) && metricKeys.length > 0 ? metricKeys : [metricKey];
    const effectiveSeriesNames =
      Array.isArray(seriesNames) && seriesNames.length > 0 ? seriesNames : effectiveMetricKeys.map(() => seriesName);

    const metricSeries = effectiveMetricKeys
      .map((key, idx) => {
        const metricData = data.find(item => item.metric === key);
        if (!metricData) return null;
        return {
          key,
          idx,
          metricData,
        };
      })
      .filter(Boolean) as Array<{ key: string; idx: number; metricData: TimeSeriesData }>;

    if (metricSeries.length === 0) return { series: [], categories: [] };

    const basePoints = metricSeries[0].metricData.points || [];
    const categories = basePoints.map(point => formatTimestamp(point.timestamp));
    const baseTimestamps = new Set(basePoints.map((p) => p.timestamp));

    const series = metricSeries.map(({ key, idx, metricData }) => {
      const points = metricData.points || [];
      const hasSameTimestamps = points.length === basePoints.length && points.every((p) => baseTimestamps.has(p.timestamp));
      if (hasSameTimestamps) {
        return {
          name: effectiveSeriesNames[idx] ?? key,
          data: points.map((p) => p.value),
        };
      }

      const valueByTs = new Map(points.map((p) => [p.timestamp, p.value]));
      return {
        name: effectiveSeriesNames[idx] ?? key,
        data: basePoints.map((p) => valueByTs.get(p.timestamp) ?? null),
      };
    });

    return { series, categories };
  };

  const resolveChartColors = (): string[] => {
    if (Array.isArray(metricKeys) && metricKeys.length > 0) {
      if (Array.isArray(chartColors) && chartColors.length > 0) {
        return metricKeys.map((_, idx) => chartColors[idx] ?? chartColor);
      }
      return metricKeys.map(() => chartColor);
    }
    return [chartColor];
  };

  const { series, categories } = prepareChartData();
  const colors = resolveChartColors();
  const annotationValues = yAxisAnnotations
    .flatMap((annotation) => [annotation.y, annotation.y2])
    .filter((value): value is number => Number.isFinite(value));
  const chartYValues = [
    ...series.flatMap((item) => item.data).filter((value): value is number => typeof value === 'number' && !Number.isNaN(value)),
    ...annotationValues,
  ];

  // Define options for dropdowns
  const timeRangeOptions = [
    { value: '1h', label: 'Last Hour' },
    { value: '6h', label: 'Last 6 Hours' },
    { value: '12h', label: 'Last 12 Hours' },
    { value: '24h', label: 'Last 24 Hours' },
    { value: '48h', label: 'Last 48 Hours' },
    { value: '7d', label: 'Last 7 Days' },
  ];

  const intervalOptions = [
    { value: '5m', label: '5 Minutes' },
    { value: '15m', label: '15 Minutes' },
    { value: '30m', label: '30 Minutes' },
    { value: '1h', label: '1 Hour' },
  ];

  const handleIntervalChange = (
    newValue: SingleValue<{value: string; label: string}>, 
    actionMeta: ActionMeta<{value: string; label: string}>
  ) => {
    if (newValue) {
      const newInterval = newValue.value;
      setSelectedInterval(newInterval);
      if (onIntervalChange) {
        onIntervalChange(newInterval);
      }
    }
  };

  const handleTimeRangeChange = (
    newValue: SingleValue<{value: string; label: string}>, 
    actionMeta: ActionMeta<{value: string; label: string}>
  ) => {
    if (newValue) {
      const range = newValue.value;
      setTimeRange(range);
      
      if (onDateRangeChange) {
        // Get current browser time
        const now = new Date();
        
        let start;
        
        switch (range) {
          case '1h':
            start = subHours(now, 1);
            break;
          case '6h':
            start = subHours(now, 6);
            break;
          case '12h':
            start = subHours(now, 12);
            break;
          case '24h':
          default:
            start = subHours(now, 24);
            break;
          case '48h':
            start = subHours(now, 48);
            break;
          case '7d':
            start = subHours(now, 168);
            break;
        }
        
        
        // JavaScript's Date.toISOString() automatically converts to UTC
        // This might be causing the issue if we're not accounting for timezone differences
        const startISO = start.toISOString();
        const nowISO = now.toISOString();
        
        
        onDateRangeChange(startISO, nowISO);
      }
    }
  };
  
  const handleTimeRangeTypeToggle = (isAbsolute: boolean) => {
    setIsAbsoluteTime(isAbsolute);
    // Save preference to localStorage
    localStorage.setItem(`timeRangeType_${deviceId}`, isAbsolute ? 'absolute' : 'relative');
  };
  
  const handleAbsoluteStartTimeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newStartTime = e.target.value;
    setAbsoluteStartTime(newStartTime);
  };
  
  const handleAbsoluteEndTimeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newEndTime = e.target.value;
    setAbsoluteEndTime(newEndTime);
  };
  
  const isValidDateTimeFormat = (dateTimeStr: string): boolean => {
    // Basic format validation: YYYY-MM-DD HH:MM or YYYY/MM/DD HH:MM
    const regex = /^\d{4}[-\/]\d{1,2}[-\/]\d{1,2}[T\s]\d{1,2}:\d{1,2}$/;
    if (!regex.test(dateTimeStr)) {
      return false;
    }
    
    // Check if it's a valid date
    const date = new Date(dateTimeStr);
    return !isNaN(date.getTime());
  };

  const applyAbsoluteTimeRange = () => {
    setTimeError(null);
    
    // Validate input format
    if (!isValidDateTimeFormat(absoluteStartTime)) {
      setTimeError("Invalid start time format. Please use YYYY-MM-DD HH:MM");
      return;
    }
    
    if (!isValidDateTimeFormat(absoluteEndTime)) {
      setTimeError("Invalid end time format. Please use YYYY-MM-DD HH:MM");
      return;
    }
    
    // Check if start time is before end time
    const startDate = new Date(absoluteStartTime);
    const endDate = new Date(absoluteEndTime);
    
    if (startDate >= endDate) {
      setTimeError("Start time must be earlier than end time");
      return;
    }
    
    if (onDateRangeChange && absoluteStartTime && absoluteEndTime) {
      try {
        // Convert local time strings to Date objects
        const startDate = new Date(absoluteStartTime);
        const endDate = new Date(absoluteEndTime);
        
        // Direct conversion to ISO string (will convert to UTC)
        const startISO = startDate.toISOString();
        const endISO = endDate.toISOString();
        
        onDateRangeChange(startISO, endISO);
      } catch (error) {
        console.error('Error converting time input to date:', error);
        setTimeError("Error processing dates. Please check your input.");
      }
    }
  };

  const chartKey = `time-series-chart-${deviceId}-${timeRange}-${selectedInterval}-${data.length}`;

  const chartOptions: ApexOptions = {
    ...apexLineChartDefaultOption,
    annotations: {
      ...(apexLineChartDefaultOption.annotations || {}),
      ...(yAxisAnnotations.length > 0 ? { yaxis: yAxisAnnotations } : {}),
    },
    chart: {
      ...apexLineChartDefaultOption.chart,
      id: 'time-series-chart',
      type: 'line',
      toolbar: {
        show: true,
      },
      zoom: {
        enabled: false,
      },
      animations: {
        enabled: true,
        speed: 800,
        animateGradually: {
          enabled: true,
          delay: 150
        },
        dynamicAnimation: {
          enabled: true,
          speed: 350
        }
      }
    },
    colors,
    title: {
      text: showTitle ? title : '',
      align: 'left',
      style: {
        fontSize: '16px',
        fontWeight: 600,
      },
    },
    subtitle: {
      text: showTitle ? `Device ID: ${deviceId}` : '',
      align: 'left',
    },
    xaxis: {
      categories,
      title: {
        text: 'Time',
      },
      labels: {
        rotate: 0,
        rotateAlways: false,
        maxHeight: 120,
        trim: false,
        hideOverlappingLabels: true,
        showDuplicates: false,
        style: {
          fontSize: '12px',
        },
        datetimeUTC: false,
        formatter: function(value) {
          try {
            // 确保值是有效的日期字符串
            if (typeof value !== 'string' || !value) {
              return ['--', '--'];
            }
            
            const date = new Date(value);
            // 检查日期是否有效
            if (isNaN(date.getTime())) {
              return ['--', '--'];
            }
            
            // 使用数组创建多行文本
            return [
              format(date, 'yyyy-MM-dd'),
              format(date, 'HH:mm')
            ];
          } catch (error) {
            console.error('Error formatting x-axis label:', error, value);
            return ['--', '--'];
          }
        }
      },
      tickAmount: 6,
      tickPlacement: 'on',
      axisTicks: {
        show: true
      },
      axisBorder: {
        show: true
      },
      tooltip: {
        enabled: true
      }
    },
    yaxis: {
      title: {
        text: yAxisTitle,
      },
      min: yMin !== undefined ? yMin : (min) => {
        const effectiveMin = chartYValues.length > 0 ? Math.min(min, ...chartYValues) : min;
        return Math.floor(effectiveMin) - 5;
      },
      ...(yTickAmount !== undefined ? { tickAmount: yTickAmount } : {}),
      ...(yTickAmount !== undefined || valueFormatter
        ? {
            labels: {
              formatter: (value: number) =>
                valueFormatter ? valueFormatter(Math.round(value)) : String(Math.round(value)),
              ...(yLabelMaxWidth !== undefined ? { maxWidth: yLabelMaxWidth } : {}),
              style: { fontSize: '12px' },
            },
          }
        : {}),
      max: yMax !== undefined ? yMax : (max) => {
        const effectiveMax = chartYValues.length > 0 ? Math.max(max, ...chartYValues) : max;
        return Math.ceil(effectiveMax) + 5;
      },
    },
    tooltip: {
      y: {
        formatter: (value) =>
          valueFormatter && typeof value === 'number'
            ? valueFormatter(Math.round(value))
            : `${value}${tooltipValueSuffix}`,
      },
    },
    markers: {
      size: 4,
      hover: {
        size: 6,
      },
    },
    stroke: {
      width: 3,
      curve,
    },
    grid: {
      borderColor: '#e0e0e0',
      row: {
        colors: ['#f8f9fa', 'transparent'],
        opacity: 0.5
      },
    },
  };

  return (
    <Card className={className}>
      <div className="card-body">
        <div className="flex flex-col mb-4">
          <div className="flex items-center gap-2 mb-3">
            <span className="tag bg-indigo-100 text-indigo-600 dark:bg-indigo-500/20 dark:text-indigo-100 border-0 h-8 flex items-center">
              <span className="font-semibold mr-1">Device:</span> {deviceId}
            </span>
          </div>
          
          <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between">
            {showTitle && (
              <div>
                <h4>{title}</h4>
                <p>{subtitle}</p>
              </div>
            )}
            
            <div className="flex flex-col gap-3 mt-3 lg:mt-0 w-full lg:w-auto ml-auto">
              <div className="flex items-center gap-2 justify-end">
                <label htmlFor="timeRangeToggle" className="font-semibold">Time Range Type:</label>
                <div className="flex items-center">
                  <span className={`cursor-pointer px-3 py-1 rounded-l-md ${!isAbsoluteTime ? 'bg-indigo-600 text-white' : 'bg-gray-200'}`} 
                    onClick={() => handleTimeRangeTypeToggle(false)}>
                    Relative
                  </span>
                  <span className={`cursor-pointer px-3 py-1 rounded-r-md ${isAbsoluteTime ? 'bg-indigo-600 text-white' : 'bg-gray-200'}`} 
                    onClick={() => handleTimeRangeTypeToggle(true)}>
                    Absolute
                  </span>
                </div>
              </div>
              
              <div className="flex flex-wrap gap-3 w-full justify-end">
                {isAbsoluteTime ? (
                  <div className="flex flex-wrap gap-3 w-full justify-end">
                    <div className="flex items-center gap-2">
                      <label htmlFor="startTime" className="font-semibold">Start:</label>
                      <DateTimePicker
                        value={absoluteStartTime ? new Date(absoluteStartTime) : null}
                        onChange={(date) => {
                          if (date) {
                            setAbsoluteStartTime(formatDateTimeForInput(date.toISOString()));
                          }
                        }}
                        placeholder="Select start date & time"
                        format="YYYY-MM-DD HH:mm"
                        className="w-[200px]"
                      />
                    </div>
                    
                    <div className="flex items-center gap-2">
                      <label htmlFor="endTime" className="font-semibold">End:</label>
                      <DateTimePicker
                        value={absoluteEndTime ? new Date(absoluteEndTime) : null}
                        onChange={(date) => {
                          if (date) {
                            setAbsoluteEndTime(formatDateTimeForInput(date.toISOString()));
                          }
                        }}
                        placeholder="Select end date & time"
                        format="YYYY-MM-DD HH:mm"
                        className="w-[200px]"
                      />
                    </div>
                    
                    <Button 
                      className="btn btn-sm btn-primary h-9 px-4 font-medium"
                      onClick={applyAbsoluteTimeRange}
                      variant="solid"
                    >
                      Apply
                    </Button>
                    
                    {timeError && (
                      <div className="text-red-500 text-sm mt-2 w-full text-right">
                        {timeError}
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <label htmlFor="timeRange" className="font-semibold">Time Range:</label>
                    <Select<{value: string; label: string}, false>
                      id="timeRange"
                      options={timeRangeOptions}
                      value={timeRangeOptions.find(option => option.value === timeRange)}
                      onChange={handleTimeRangeChange}
                      className="min-w-[150px]"
                      size="sm"
                    />
                  </div>
                )}
                
                <div className="flex items-center gap-2">
                  <label htmlFor="interval" className="font-semibold">Interval:</label>
                  <Select<{value: string; label: string}, false>
                    id="interval"
                    options={intervalOptions}
                    value={intervalOptions.find(option => option.value === selectedInterval)}
                    onChange={handleIntervalChange}
                    className="min-w-[150px]"
                    size="sm"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {series.length > 0 ? (
          <div className="chartRef w-full">
            <ApexChart
              key={chartKey}
              options={chartOptions}
              series={series}
              type="line"
              height={height}
              width="100%"
            />
          </div>
        ) : (
          <div className="flex justify-center items-center h-96">
            <p>No data available</p>
          </div>
        )}
      </div>
    </Card>
  );
};

export default TimeSeriesChart; 
